import { first, Observable, of, Subject, switchMap } from 'rxjs';
import { CanDeactivateFn, RouterStateSnapshot } from '@angular/router';
import { inject, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    SaveWarningDialogComponent,
    SaveWarningDialogData,
} from '@r1-compass-library/components/dialog/save-warning-dialog/save-warning-dialog.component';
import { SaveGuardStore } from './save-guard.store';
import { MutationResult } from '@angular-architects/ngrx-toolkit';

/**
 * @description Else case for attempting a save mutation.
 * Like narrowing the entity of `T | undefined` to `T` and we need a sensible fallback.
 *
 * @example
 * onSave: () => {
 *   if (this.entityThatCouldBeUndefinedButSurelyIsDefined) {
 *     return this.saveEntity(this.entityThatCouldBeUndefinedButSurelyIsDefined);
 *   } else {
 *     return noopFormSaveMutationResult();
 *   }
 */
export function noopFormSaveMutationResult(): Promise<MutationResult<void>> {
    return Promise.resolve({ status: 'aborted' });
}

/**
 * @description The minimum required fields for any single form save warning.
 * Used for most @see {FormUnsavedChangesWarningGeneric} which add on gauranteed `onSave` fns,
 * and then otherwise this is the minimum base for `'formUnsavedChangesWarningMisc` which have
 * their own weird edgecases.
 */
export type SaveGuardHandlerName =
    | 'formUnsavedChangesWarningEntityDefinition'
    | 'formUnsavedChangesWarningDivisionSettings'
    | 'formUnsavedChangesWarningContentLibraryRules'
    | 'formUnsavedChangesWarningMisc';

type SaveCapableGuardHandlerName = Exclude<SaveGuardHandlerName, 'formUnsavedChangesWarningMisc'>;

export interface FormUnsavedChangesWarningBase<T extends SaveGuardHandlerName> {
    /**
     * @description All the save guard state and function and side effects are jammed in this function.
     * And then this function and its arguments should be suppllied in the constructor.
     *
     * @example
     * saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningEntityDefinition;
     * constructor() {
     *   this.saveGuardHandlerFn({respectiveFieldA: ..., respectiveFieldB: ...});
     * }
     */
    saveGuardHandlerFn: InstanceType<typeof SaveGuardStore>[T];
}

interface FormUnsavedChangesWarningGeneric<T extends SaveCapableGuardHandlerName> {
    saveGuardHandlerFn: FormUnsavedChangesWarningBase<T>['saveGuardHandlerFn'];

    /**
     * @description The actual function that does form saving. The `saveGuardHandlerFn` often will refer to this function as an arg.
     *
     * Three cases this covers.
     * 1) (`unknown` case #1) `Promise<MutationResult<any>>` for entities
     * 2) (`unknown` case #2) `Promise<MutationResult<void>>`, see {@link noopFormSaveMutationResult} for cases where an `else` is needed for narrowing the save's data, but the case won't actually be reached in practice
     * 3) `Promise<{ submitted: boolean }>` for waiting on the signal slice saves
     */
    onSave: () => Promise<MutationResult<unknown>> | Promise<{ submitted: boolean }>;

    readonly allowPartialSave?: boolean;
}

/**
 * @description Entities ranging from play/template/lifecycle/content pages
 * @see {@link FormUnsavedChangesWarningGeneric}
 */
export type FormUnsavedChangesWarningEntity =
    FormUnsavedChangesWarningGeneric<'formUnsavedChangesWarningEntityDefinition'> & {
        /**
         * @example canDeactivate$ = formUnsaved(this.formName); // fully reactive from form
         * @example canDeactivate$ = new BehaviorSubject({ dirtied: false, valid: true }); // manual handling needed
         */
        canDeactivate$: Observable<{
            dirtied: boolean;
            valid: boolean;
            disableSave?: boolean;
        }>;
        allowPartialSave: boolean;
        disableSave?: Signal<boolean>;
    };

/**
 * @description Any division settings page
 * @see {@link FormUnsavedChangesWarningGeneric}
 */
export type FormUnsavedChangesWarningDivisionSettings =
    FormUnsavedChangesWarningGeneric<'formUnsavedChangesWarningDivisionSettings'> & {
        /**
         * @example canDeactivate$ = formUnsaved(this.formName); // fully reactive from form
         * @example canDeactivate$ = new BehaviorSubject({ dirtied: false, valid: true }); // manual handling needed
         */
        canDeactivate$: Observable<{
            dirtied: boolean;
            valid: boolean;
        }>;
    };

/**
 * @description Content library rules are a weird edgecase with no traditional "form", but are otherwise
 * similar to the generic interface
 * @see {@link FormUnsavedChangesWarningGeneric}
 */
export type FormUnsavedChangesWarningContentLibraryRules =
    FormUnsavedChangesWarningGeneric<'formUnsavedChangesWarningContentLibraryRules'> & {
        saved$: Subject<void>;
    } & {
        /**
         * @example canDeactivate$ = formUnsaved(this.formName); // fully reactive from form
         * @example canDeactivate$ = new BehaviorSubject({ dirtied: false, valid: true }); // manual handling needed
         */
        canDeactivate$: Observable<{
            dirtied: boolean;
            valid: boolean;
        }>;
    };

export type SaveGuardCallback =
    | {
          confirm: boolean;
          type: 'confirm' | 'cancel' | 'confirmWithoutSave';
      }
    | undefined;

/**
 * @description Determines whether a save operation completed successfully before allowing navigation.
 */
export function wasSaveSuccessful(result: MutationResult<unknown> | { submitted: boolean }): boolean {
    return 'status' in result ? result.status === 'success' : result.submitted;
}

export function unsavedChangesHandler(
    nextState: RouterStateSnapshot,
    res: (response: SaveGuardCallback) => Observable<boolean> | Promise<boolean>,
    confirm = true,
    allowPartialSave = false,
    enablePageStatusChangedWithoutSave: undefined | boolean = undefined
) {
    const dialog = inject(MatDialog);
    const formUnsavedStore = inject(SaveGuardStore);

    return formUnsavedStore.canDeactivate$.pipe(
        first(),
        switchMap(hasChanges => {
            // Only for "Enable" pages
            if (enablePageStatusChangedWithoutSave !== undefined && !enablePageStatusChangedWithoutSave) {
                // Nothing was changed since the last save, so partial-save forms can navigate even when incomplete.
                return of(true);
            } else if (enablePageStatusChangedWithoutSave) {
                return showWarning();
            }

            // For everything else
            if (!hasChanges.dirtied && (hasChanges.valid || allowPartialSave)) {
                // Nothing was changed since the last save, so partial-save forms can navigate even when incomplete.
                return of(true);
            } else if (nextState.url === '/login') {
                return of(true);
            } else {
                return showWarning();
            }

            function showWarning() {
                return dialog
                    .open<
                        SaveWarningDialogComponent,
                        SaveWarningDialogData,
                        { confirm: boolean; type: 'confirm' | 'cancel' | 'confirmWithoutSave' }
                    >(SaveWarningDialogComponent, {
                        width: '400px',
                        disableClose: true,
                        data: {
                            allowPartialSave: allowPartialSave,
                            hasConfirm: confirm,
                            hasCancel: true,
                            hasConfirmWithoutSave: true,
                            valid: hasChanges.valid,
                            disableSave: hasChanges.disableSave,
                        },
                    })
                    .afterClosed()
                    .pipe(
                        switchMap(response => {
                            return res(response);
                        })
                    );
            }
        })
    );
}

const createUnsavedChangesGuard = <T extends SaveCapableGuardHandlerName>(
    allowPartialSave: boolean
): CanDeactivateFn<FormUnsavedChangesWarningGeneric<T>> => {
    return (component, _currentRoute, _routerStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> => {
        const responseCallback = (response: SaveGuardCallback) => {
            if (response?.type === 'confirm') {
                return component.onSave().then(wasSaveSuccessful, () => false);
            }
            return response?.type === 'confirmWithoutSave' ? of(true) : of(false);
        };
        return unsavedChangesHandler(nextState, responseCallback, true, allowPartialSave);
    };
};

export const hasUnsavedChanges = createUnsavedChangesGuard<SaveCapableGuardHandlerName>(false);

export const hasUnsavedChangesAllowPartialSave = createUnsavedChangesGuard<SaveCapableGuardHandlerName>(true);
